import { useMemo, useState } from 'react';

import { Box, Skeleton, Tooltip, Typography } from '@mui/material';
import { ApexOptions } from 'apexcharts';
import { groupBy } from 'lodash';
import ReactApexChart from 'react-apexcharts';
import { useQuery } from 'react-query';
import { If } from 'src/components/If';
import Subtitle from 'src/components/Subtitle';
import { getQuotaValuesDataCharts } from 'src/services/operation/operationFidc';

import { GroupButtonPeriod, Period } from '../../components/GroupButtonPeriod';
import {
  colors,
  getLimitZoomMountedHandler,
  getPeriodDate,
  getQuotaKey,
  getQuotaLabel,
  locale,
  noData,
  QuotaAccumulator,
} from '../utils';

const defaultPeriodInDays = 180;

export function NetAssetValueUnitCharts({ hashId }: { hashId: string }) {
  const [selectedQuotas, setSelectedQuotas] = useState<string[]>([]);
  const [selectedPeriod, setSelectedPeriod] =
    useState<Period>(defaultPeriodInDays);
  const [hoveredTotalPL, setHoveredTotalPL] = useState<number | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['net-asset-value-unit', hashId, selectedPeriod],
    queryFn: () =>
      getQuotaValuesDataCharts(hashId, getPeriodDate(selectedPeriod)),
    onSuccess: (data) => {
      const groupedByQuotaSerie = groupBy(data, getQuotaKey);
      setSelectedQuotas(Object.keys(groupedByQuotaSerie));
    },
    enabled: !!hashId,
    refetchOnWindowFocus: false,
  });

  const handlePeriodChange = (days: Period) => {
    setSelectedPeriod(days);
  };

  const chartData = useMemo(() => {
    if (!data || data.length === 0 || selectedQuotas.length === 0) {
      return { series: [] };
    }

    const filteredData = data.filter((item) =>
      selectedQuotas.includes(getQuotaKey(item)),
    );

    const allDates = [...new Set(filteredData.map((item) => item.data))].sort();

    const groupedByQuota = filteredData.reduce(
      (acc: QuotaAccumulator, item) => {
        const key = getQuotaKey(item);
        if (!acc[key]) {
          acc[key] = {
            name: getQuotaLabel(item.cota, item.serie),
            values: {},
          };
        }
        acc[key].values[item.data] = item.plCota;
        return acc;
      },
      {} as QuotaAccumulator,
    );

    const totalPLByDate: Record<string, number> = {};
    allDates.forEach((date) => {
      let totalPL = 0;
      Object.values(groupedByQuota).forEach((quotaValues) => {
        if (quotaValues.values[date]) {
          totalPL += quotaValues.values[date];
        }
      });
      totalPLByDate[date] = totalPL;
    });

    const totalSeries = {
      name: 'PL Total',
      data: allDates.map((date) => {
        const timestamp = new Date(date).getTime();
        const value = totalPLByDate[date];
        return {
          x: timestamp,
          y: value || null,
        };
      }),
    };

    const quotaSeries = Object.values(groupedByQuota).map(
      ({ name, values }) => ({
        name,
        data: allDates.map((date) => {
          const timestamp = new Date(date).getTime();
          const value = values[date];
          return {
            x: timestamp,
            y: value || null,
          };
        }),
      }),
    );

    // 2025-10-14 - Luiz Gustavo Saraiva
    // Ordenar séries: No stacked, a primeira série fica no fundo
    // Queremos: Sênior no topo, Mezanino no meio, Subordinada embaixo
    // Então invertemos: Subordinada primeiro, Mezanino, depois Sênior
    // Esse comportamento é necessário para que o gráfico seja exibido corretamente pois o stacked é por default em ordem crescente
    const orderedSeries = quotaSeries.sort((a, b) => {
      const getPriority = (name: string) => {
        const nameLower = name.toLowerCase();
        if (nameLower.includes('subordinada')) return 1;
        if (nameLower.includes('mezanino')) return 2;
        if (nameLower.includes('sênior') || nameLower.includes('senior'))
          return 3;
        return 4;
      };

      return getPriority(a.name) - getPriority(b.name);
    });

    return { series: orderedSeries, totalSeries };
  }, [data, selectedQuotas]);

  const options: ApexOptions = {
    series: chartData.series,
    colors: colors,
    chart: {
      type: 'area',
      defaultLocale: 'pt-BR',
      locales: [locale],
      zoom: {
        type: 'xy',
        enabled: true,
        autoScaleYaxis: false,
      },
      toolbar: {
        autoSelected: 'zoom',
      },
      stacked: true,
      events: {
        updated: getLimitZoomMountedHandler(),
        mouseMove: function (_, chartContext, config) {
          if (
            config.dataPointIndex >= 0 &&
            chartData.totalSeries &&
            chartData.totalSeries.data[config.dataPointIndex]
          ) {
            const totalValue = chartData.totalSeries.data[config.dataPointIndex]
              .y as number;
            setHoveredTotalPL(totalValue);
          }
        },
        mouseLeave: function () {
          setHoveredTotalPL(null);
        },
      },
    },
    stroke: {
      curve: 'smooth',
      width: 2,
    },
    fill: {
      type: 'gradient',
      gradient: {
        opacityFrom: 0.6,
        opacityTo: 0.1,
      },
    },
    dataLabels: {
      enabled: false,
    },
    markers: {
      size: 0,
      hover: {
        size: 6,
      },
    },
    legend: {
      show: true,
      inverseOrder: true,
      showForSingleSeries: true,
      position: 'top',
      horizontalAlign: 'center',
      floating: false,
      offsetY: 20,
      height: 50,
      itemMargin: {
        horizontal: 10,
        vertical: 5,
      },
    },
    yaxis: {
      labels: {
        formatter: function (val) {
          if (val === null || val === undefined) return '';
          return val.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            style: 'currency',
            currency: 'BRL',
          });
        },
      },
      title: {
        text: 'Patrimônio Líquido (R$)',
        style: {
          fontFamily: 'Inter',
        },
      },
    },
    xaxis: {
      type: 'datetime',
    },
    tooltip: {
      shared: true,
      intersect: false,
      x: {
        format: 'dd/MM/yyyy',
      },
      inverseOrder: true,
    },
    noData: noData,
  };

  const latestTotalPL = useMemo(() => {
    if (!chartData.totalSeries || chartData.totalSeries.data.length === 0) {
      return null;
    }
    const sortedData = [...chartData.totalSeries.data].sort(
      (a, b) => (b.x as number) - (a.x as number),
    );
    return sortedData[0]?.y;
  }, [chartData.totalSeries]);

  const displayTotalPL =
    hoveredTotalPL !== null ? hoveredTotalPL : latestTotalPL;

  return (
    <Box sx={{ '.apexcharts-menu-item.exportCSV': { display: 'none' } }}>
      <Subtitle title="Patrimônio Líquido" />
      <If condition={isLoading}>
        <Skeleton variant="rectangular" animation="wave" height={420} />
      </If>
      <If condition={!isLoading}>
        <Box
          width="100%"
          display="flex"
          flexDirection="column"
          gap={2}
          alignItems="flex-end">
          <GroupButtonPeriod
            selectedPeriod={selectedPeriod}
            handlePeriodChange={handlePeriodChange}
          />
        </Box>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          gap={1}
          mb={2}>
          <Tooltip title="Patrimônio líquido total">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                padding: '8px 16px',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
                border: '1px solid #e0e0e0',
              }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                PL Total:
              </Typography>

              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {displayTotalPL !== null
                  ? displayTotalPL.toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    })
                  : '-'}
              </Typography>
            </Box>
          </Tooltip>
        </Box>
        <ReactApexChart
          type="area"
          series={chartData.series}
          options={options}
          height={420}
        />
      </If>
    </Box>
  );
}
